/*
 * Normalizes Markdown pasted from outside the editor (e.g. a .md file written
 * by Claude or another tool) into the Markdown the prompt editor formats:
 *
 * - "* item" and "+ item" bullets become "- item"
 * - nested list items indented by any consistent amount (3 or 4 spaces, tabs)
 *   become 2-space nesting levels
 * - "1) item" becomes "1. item"
 * - "__bold__" becomes "**bold**"
 * - tables become "| a | b |" rows with trimmed cells (column padding and
 *   missing outer pipes are common in generated Markdown); short rows get
 *   empty cells, and a row with extra cells widens the table rather than
 *   losing its text
 *
 * Only used for pasted plain text. Stored prompts are never normalized when
 * they load, and copies from this editor are pasted unchanged, so saved text
 * is never altered by it. Code blocks are left exactly as they are, and so is
 * everything the editor keeps as plain text (#### headings, ---, tables,
 * links).
 */

const LIST_LINE = /^([ \t]*)([-*+]|\d{1,9}[.)])[ \t]+(.*)$/;
// "---", "***", "* * *": dividers, not list items
const DIVIDER_LINE = /^[ \t]*([-*_])(?:[ \t]*\1){2,}[ \t]*$/;
const CODE_FENCE = /^[ \t]*(```|~~~)/;
const INLINE_CODE = /(`[^`]+`)/;
// Same boundaries as "_italic_" in prompt-markdown.ts, so snake_case names
// such as {{user.first_name}} are never touched
const UNDERSCORE_BOLD = /(^|[^_\w])__(\S(?:.*?\S)??)__(?![_\w])/g;

const TAB_WIDTH = 4;

const indentWidth = (indent: string) =>
  Array.from(indent).reduce(
    (width, character) => width + (character === '\t' ? TAB_WIDTH : 1),
    0
  );

const normalizeBold = (line: string) =>
  line
    .split(INLINE_CODE)
    .map((part, index) =>
      // Odd parts are `code` spans: left as they are
      index % 2 === 1 ? part : part.replace(UNDERSCORE_BOLD, '$1**$2**')
    )
    .join('');

/** Cells of a table row, with or without outer pipes; null if not a row. */
const tableCells = (line: string): string[] | null => {
  if (!line.includes('|')) return null;
  let body = line.trim();
  if (body.startsWith('|')) body = body.slice(1);
  if (body.endsWith('|') && !body.endsWith('\\|')) body = body.slice(0, -1);

  const cells: string[] = [];
  let current = '';
  for (let index = 0; index < body.length; index += 1) {
    const character = body[index];
    if (character === '\\' && body[index + 1] === '|') {
      current += '\\|';
      index += 1;
    } else if (character === '|') {
      cells.push(current);
      current = '';
    } else {
      current += character;
    }
  }
  cells.push(current);
  return cells.map((cell) => cell.trim());
};

const SEPARATOR_CELL = /^:?-+:?$/;

const separatorCell = (cell: string) =>
  `${cell.startsWith(':') ? ':' : ''}---${cell.length > 1 && cell.endsWith(':') ? ':' : ''}`;

const tableRow = (cells: string[], columns: number) =>
  `| ${Array.from({ length: columns }, (_, index) =>
    normalizeBold(cells[index] ?? '')
  ).join(' | ')} |`;

export const normalizePastedMarkdown = (text: string): string => {
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  const output: string[] = [];
  // Indentation of the list items above the current one, one per level
  let levels: number[] = [];
  let inCodeBlock = false;

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];

    if (CODE_FENCE.test(line)) {
      inCodeBlock = !inCodeBlock;
      levels = [];
      output.push(line);
      continue;
    }
    if (inCodeBlock) {
      output.push(line);
      continue;
    }

    // A table: a header row followed by a "--- | ---" separator row
    const header = tableCells(line);
    const separator =
      header && index + 1 < lines.length ? tableCells(lines[index + 1]) : null;
    if (
      header &&
      separator &&
      separator.length === header.length &&
      separator.every((cell) => SEPARATOR_CELL.test(cell))
    ) {
      levels = [];
      const rows: string[][] = [];
      index += 2;
      while (index < lines.length && lines[index].trim()) {
        const cells = tableCells(lines[index]);
        if (!cells) break;
        rows.push(cells);
        index += 1;
      }
      index -= 1;

      const columns = Math.max(header.length, ...rows.map((row) => row.length));
      output.push(tableRow(header, columns));
      output.push(
        `| ${Array.from({ length: columns }, (_, column) =>
          separatorCell(separator[column] ?? '---')
        ).join(' | ')} |`
      );
      rows.forEach((row) => output.push(tableRow(row, columns)));
      continue;
    }

    const item = DIVIDER_LINE.test(line) ? null : line.match(LIST_LINE);
    if (!item) {
      levels = [];
      output.push(normalizeBold(line));
      continue;
    }

    const [, indent, marker, content] = item;
    const width = indentWidth(indent);
    while (levels.length && levels[levels.length - 1] > width) levels.pop();
    if (!levels.length || levels[levels.length - 1] < width) {
      levels.push(width);
    }
    const level = levels.length - 1;

    const listMarker = /\d/.test(marker) ? `${marker.slice(0, -1)}. ` : '- ';
    output.push('  '.repeat(level) + listMarker + normalizeBold(content));
  }

  return output.join('\n');
};
